import { useState, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase/client';
import {
  InventoryAdjustment,
  AdjustmentFilters,
  ReasonCode,
  CorrectionRequest,
} from '@/types/inventory-adjustments';

export const useInventoryAdjustments = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch inventory adjustments with filters and pagination
  const fetchAdjustments = useCallback(async (
    filters: AdjustmentFilters = {},
    page: number = 1,
    perPage: number = 20
  ): Promise<{ data: InventoryAdjustment[]; total: number; page: number; per_page: number }> => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('inventory_adjust')
        .select(`
          *,
          warehouse:warehouse_id(id, code, name),
          prepared_by_user:prepared_by(id, full_name, email),
          approved_by_user:approved_by(id, full_name, email),
          lines:inventory_adjust_line(
            *,
            item:item_id(code, name),
            lot:lot_id(lot_code),
            serial:serial_id(serial_no),
            bin:bin_id(code, name)
          )
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.search) {
        query = query.or(`adjust_no.ilike.%${filters.search}%,reason_desc.ilike.%${filters.search}%`);
      }
      if (filters.warehouse_id) {
        query = query.eq('warehouse_id', filters.warehouse_id);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.reason_code) {
        query = query.eq('reason_code', filters.reason_code);
      }
      if (filters.date_from) {
        query = query.gte('adjust_date', filters.date_from);
      }
      if (filters.date_to) {
        query = query.lte('adjust_date', filters.date_to);
      }
      if (filters.prepared_by) {
        query = query.eq('prepared_by', filters.prepared_by);
      }

      // Apply pagination
      const from = (page - 1) * perPage;
      const to = from + perPage - 1;
      query = query.range(from, to);

      const { data, error: fetchError, count } = await query;

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      // Transform data to match interface
      const transformedData: InventoryAdjustment[] = data?.map(item => ({
        ...item,
        warehouse_name: item.warehouse?.name,
        warehouse_code: item.warehouse?.code,
        prepared_by_user: item.prepared_by_user,
        approved_by_user: item.approved_by_user,
        lines: item.lines?.map((line: any) => ({
          ...line,
          item_code: line.item?.code,
          item_name: line.item?.name,
          lot_code: line.lot?.lot_code,
          serial_no: line.serial?.serial_no,
          bin_code: line.bin?.code,
          bin_name: line.bin?.name
        }))
      })) || [];

      return {
        data: transformedData,
        total: count || 0,
        page,
        per_page: perPage
      };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch adjustments';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      return { data: [], total: 0, page, per_page: perPage };
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch single adjustment by ID
  const fetchAdjustment = useCallback(async (id: string): Promise<InventoryAdjustment | null> => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('inventory_adjust')
        .select(`
          *,
          warehouse:warehouse_id(id, code, name),
          prepared_by_user:prepared_by(id, full_name, email),
          approved_by_user:approved_by(id, full_name, email),
          lines:inventory_adjust_line(
            *,
            item:item_id(code, name),
            lot:lot_id(lot_code),
            serial:serial_id(serial_no),
            bin:bin_id(code, name)
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      if (!data) return null;

      return {
        ...data,
        warehouse_name: data.warehouse?.name,
        warehouse_code: data.warehouse?.code,
        prepared_by_user: data.prepared_by_user,
        approved_by_user: data.approved_by_user,
        lines: data.lines?.map((line: any) => ({
          ...line,
          item_code: line.item?.code,
          item_name: line.item?.name,
          lot_code: line.lot?.lot_code,
          serial_no: line.serial?.serial_no,
          bin_code: line.bin?.code,
          bin_name: line.bin?.name
        }))
      };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch adjustment';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Create new adjustment
  const createAdjustment = useCallback(async (request: any): Promise<string | null> => {
    setLoading(true);
    setError(null);

    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // Call RPC function to create adjustment with lines
      const { data, error } = await supabase.rpc('fn_create_inventory_adjustment', {
        p_warehouse_id: request.adjustment.warehouse_id,
        p_reason_code: request.adjustment.reason_code,
        p_reason_desc: request.adjustment.reason_desc,
        p_adjust_date: request.adjustment.adjust_date,
        p_note: request.adjustment.note,
        p_lines: request.adjustment.lines
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Success',
        description: 'Inventory adjustment created successfully',
        variant: 'default'
      });

      return data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create adjustment';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Update adjustment (only for DRAFT status)
  const updateAdjustment = useCallback(async (id: string, request: any): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Call RPC function to update adjustment
      const { error } = await supabase.rpc('fn_update_inventory_adjustment', {
        p_adjust_id: id,
        p_warehouse_id: request.adjustment.warehouse_id,
        p_reason_code: request.adjustment.reason_code,
        p_reason_desc: request.adjustment.reason_desc,
        p_adjust_date: request.adjustment.adjust_date,
        p_note: request.adjustment.note,
        p_lines: request.adjustment.lines
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Success',
        description: 'Adjustment updated successfully',
        variant: 'default'
      });

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update adjustment';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Post adjustment to inventory
  const postAdjustment = useCallback(async (request: any): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Call RPC function to post adjustment
      const { error } = await supabase.rpc('fn_post_inventory_adjustment', {
        p_adjust_id: request.adjust_id,
        p_post_to_inventory: request.post_to_inventory,
        p_approval_note: request.approval_note
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Success',
        description: 'Adjustment posted to inventory successfully',
        variant: 'default'
      });

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to post adjustment';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Delete adjustment (only DRAFT status)
  const deleteAdjustment = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('inventory_adjust')
        .delete()
        .eq('id', id)
        .eq('status', 'DRAFT');

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Success',
        description: 'Adjustment deleted successfully',
        variant: 'default'
      });

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete adjustment';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch reason codes
  const fetchReasonCodes = useCallback(async (): Promise<ReasonCode[]> => {
    try {
      const { data, error } = await supabase
        .from('inventory_adjust_reasons')
        .select('*')
        .eq('is_active', true)
        .order('code');

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (err: any) {
      console.error('Failed to fetch reason codes:', err);
      return [];
    }
  }, []);

  // Fetch adjustment summary/statistics
  const fetchAdjustmentSummary = useCallback(async (
    dateFrom?: string,
    dateTo?: string
  ): Promise<any | null> => {
    setLoading(true);

    try {
      const { data, error } = await supabase.rpc('fn_get_adjustment_summary', {
        p_date_from: dateFrom,
        p_date_to: dateTo
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch summary';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Stock correction functions
  const createCorrection = useCallback(async (request: CorrectionRequest): Promise<string | null> => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc('fn_create_stock_correction', request);

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Success',
        description: 'Stock correction request created successfully',
        variant: 'default'
      });

      return data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create correction';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const approveCorrectionRequest = useCallback(async (request: any): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.rpc('fn_approve_stock_correction', request);

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Success',
        description: `Stock correction ${request.action.toLowerCase()}d successfully`,
        variant: 'default'
      });

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to process correction';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  return {
    loading,
    error,
    fetchAdjustments,
    fetchAdjustment,
    createAdjustment,
    updateAdjustment,
    postAdjustment,
    deleteAdjustment,
    fetchReasonCodes,
    fetchAdjustmentSummary,

    createCorrection,
    approveCorrectionRequest
  };
};
